// components/HomeContent.jsx
import React from 'react';
import { 
  FaChartLine, 
  FaDatabase, 
  FaFileExcel, 
  FaSearch, 
  FaShieldAlt,
  FaRocket,
  FaUsers,
  FaGlobe
} from 'react-icons/fa';

const HomeContent = () => {
  const features = [
    {
      icon: <FaChartLine className="text-3xl text-blue-500" />,
      title: "Advanced Analytics",
      description: "Powerful financial analysis tools with real-time data visualization and insights.",
      color: "bg-blue-50 dark:bg-blue-900/20"
    },
    {
      icon: <FaDatabase className="text-3xl text-green-500" />,
      title: "Financial Database",
      description: "Access to comprehensive financial statements of thousands of companies worldwide.",
      color: "bg-green-50 dark:bg-green-900/20"
    },
    {
      icon: <FaFileExcel className="text-3xl text-purple-500" />,
      title: "Excel Integration",
      description: "Seamlessly upload and analyze Excel financial statements with automated processing.",
      color: "bg-purple-50 dark:bg-purple-900/20"
    },
    {
      icon: <FaSearch className="text-3xl text-orange-500" />,
      title: "Smart Search",
      description: "Intelligent company search by name, ticker, or sector with instant results.",
      color: "bg-orange-50 dark:bg-orange-900/20"
    },
    {
      icon: <FaShieldAlt className="text-3xl text-red-500" />,
      title: "Secure Platform",
      description: "Bank-level security with encrypted data storage and secure user authentication.",
      color: "bg-red-50 dark:bg-red-900/20"
    },
    {
      icon: <FaRocket className="text-3xl text-indigo-500" />,
      title: "Fast Processing",
      description: "Lightning-fast data processing and analysis with optimized algorithms.",
      color: "bg-indigo-50 dark:bg-indigo-900/20"
    }
  ];

  const quickActions = [
    {
      title: "Search Company",
      description: "Find and analyze company financials",
      icon: <FaSearch className="text-blue-500" />,
      color: "hover:bg-blue-50 dark:hover:bg-blue-900/20",
      path: "#"
    },
    {
      title: "Upload Statements",
      description: "Upload Excel financial statements",
      icon: <FaFileExcel className="text-green-500" />,
      color: "hover:bg-green-50 dark:hover:bg-green-900/20",
      path: "#"
    },
    {
      title: "View Reports",
      description: "Generate financial reports",
      icon: <FaChartLine className="text-purple-500" />,
      color: "hover:bg-purple-50 dark:hover:bg-purple-900/20",
      path: "#"
    }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 mb-8 text-white">
        <div className="flex flex-col lg:flex-row items-center justify-between">
          <div className="lg:w-2/3 mb-8 lg:mb-0">
            <h1 className="text-4xl font-bold mb-4">Welcome to NIRAFINHUB</h1>
            <p className="text-xl mb-6 opacity-90">
              Your comprehensive financial analysis platform for intelligent investment decisions
            </p>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <FaUsers className="mr-2" />
                <span>Trusted by 10,000+ analysts</span>
              </div>
              <div className="flex items-center">
                <FaGlobe className="mr-2" />
                <span>Global market coverage</span>
              </div>
            </div>
          </div>
          <div className="lg:w-1/3 flex justify-center">
            <div className="bg-white/20 backdrop-blur-sm p-6 rounded-xl">
              <FaChartLine className="text-6xl mx-auto" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quickActions.map((action, index) => (
            <div
              key={index}
              className={`bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 ${action.color} transition-all duration-300 cursor-pointer transform hover:-translate-y-1`}
            >
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-700 mr-4">
                  {action.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                  {action.title}
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                {action.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Features Grid */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Platform Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`${feature.color} rounded-xl p-6 border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:shadow-lg`}
            >
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Getting Started Guide */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Getting Started</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex">
            <div className="mr-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 font-bold">
                1
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                Search for a Company
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Use the search bar at the top to find companies by name or ticker symbol.
              </p>
            </div>
          </div>
          
          <div className="flex">
            <div className="mr-4">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center text-green-600 dark:text-green-300 font-bold">
                2
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                Analyze Financial Data
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                View income statements, balance sheets, and cash flow statements in full-screen mode.
              </p>
            </div>
          </div>
          
          <div className="flex">
            <div className="mr-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center text-purple-600 dark:text-purple-300 font-bold">
                3
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
                Upload Custom Data
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Upload your own Excel financial statements for custom analysis and reporting.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">5000+</div>
          <div className="text-gray-600 dark:text-gray-400">Companies</div>
        </div>
        <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">10M+</div>
          <div className="text-gray-600 dark:text-gray-400">Data Points</div>
        </div>
        <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">24/7</div>
          <div className="text-gray-600 dark:text-gray-400">Availability</div>
        </div>
        <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="text-3xl font-bold text-orange-600 dark:text-orange-400 mb-2">99.9%</div>
          <div className="text-gray-600 dark:text-gray-400">Accuracy</div>
        </div>
      </div>
    </div>
  );
};

export default HomeContent;